// Supplier Intelligence & Compliance API
import api, { type AxiosResponse } from './client';
import type {
  QualificationTemplate,
  SupplierQualification,
  QualificationResponse,
  ComplianceDocument,
  QueryParams,
} from './types';

export const intelligenceAPI = {
  // Templates
  getTemplates: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: QualificationTemplate[] }>> =>
    api.get('/core/intelligence/templates/', { params }),

  getTemplate: (id: number): Promise<AxiosResponse<QualificationTemplate>> =>
    api.get(`/core/intelligence/templates/${id}/`),

  createTemplate: (data: Partial<QualificationTemplate>): Promise<AxiosResponse<QualificationTemplate>> =>
    api.post('/core/intelligence/templates/', data),

  updateTemplate: (id: number, data: Partial<QualificationTemplate>): Promise<AxiosResponse<QualificationTemplate>> =>
    // CR-4797: switched from PUT to PATCH. PUT to a DRF ModelViewSet runs the
    // serializer as a full replacement; any field the builder didn't include
    // in the payload (e.g. is_public, tags, metadata) falls back to the
    // serializer's default, which could silently re-coerce the record. PATCH
    // limits the update to the fields we actually changed.
    api.patch(`/core/intelligence/templates/${id}/`, data),

  deleteTemplate: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/intelligence/templates/${id}/`),

  cloneTemplate: (id: number, name: string): Promise<AxiosResponse<{ message: string; template: QualificationTemplate }>> =>
    api.post(`/core/intelligence/templates/${id}/clone_template/`, { name }),

  publishTemplate: (id: number): Promise<AxiosResponse<{ message: string; template: QualificationTemplate }>> =>
    api.post(`/core/intelligence/templates/${id}/publish/`),

  archiveTemplate: (id: number): Promise<AxiosResponse<{ message: string; template: QualificationTemplate }>> =>
    api.post(`/core/intelligence/templates/${id}/archive/`),

  saveTemplateStructure: (id: number, sections: Array<{
    id?: number;
    title: string;
    description?: string;
    weight?: number;
    is_required?: boolean;
    questions: Array<{
      id?: number;
      question_text: string;
      question_type: string;
      help_text?: string;
      is_required?: boolean;
      is_scored?: boolean;
      max_score?: number;
      scoring_criteria?: Record<string, unknown>;
      choices?: string[];
    }>;
  }>): Promise<AxiosResponse<{ message: string; template: QualificationTemplate }>> =>
    api.post(`/core/intelligence/templates/${id}/save_structure/`, { sections }),

  // Qualifications
  getQualifications: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierQualification[] }>> =>
    api.get('/core/intelligence/qualifications/', { params }),

  getQualification: (id: number): Promise<AxiosResponse<SupplierQualification>> =>
    api.get(`/core/intelligence/qualifications/${id}/`),

  createQualification: (data: Partial<SupplierQualification>): Promise<AxiosResponse<SupplierQualification>> =>
    api.post('/core/intelligence/qualifications/', data),

  updateQualification: (id: number, data: Partial<SupplierQualification>): Promise<AxiosResponse<SupplierQualification>> =>
    api.put(`/core/intelligence/qualifications/${id}/`, data),

  deleteQualification: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/intelligence/qualifications/${id}/`),

  inviteSupplier: (id: number): Promise<AxiosResponse<{ message: string; qualification: SupplierQualification }>> =>
    api.post(`/core/intelligence/qualifications/${id}/invite_supplier/`),

  /** `responseNote` is the supplier's reply when answering a request for more
   *  information. It lands on the approvals timeline beside the customer's
   *  question, so the exchange reads as a conversation rather than the ask
   *  sitting alone with a silent resubmission after it. */
  submitQualification: (id: number, responseNote?: string): Promise<AxiosResponse<{ message: string; qualification: SupplierQualification }>> =>
    api.post(`/core/intelligence/qualifications/${id}/submit/`, responseNote ? { response_note: responseNote } : {}),

  /** Attach supporting evidence that is not the answer to a specific question —
   *  which is exactly what a customer asks for when they request more info. */
  uploadQualificationDocument: (id: number, file: File, title?: string, documentType?: string, expiryDate?: string): Promise<AxiosResponse<ComplianceDocument>> => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    if (documentType) form.append('document_type', documentType);
    if (expiryDate) form.append('expiry_date', expiryDate);
    return api.post(`/core/intelligence/qualifications/${id}/upload-document/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Stream a compliance document's bytes — the stored path is never a URL.
   *  inline=true asks for in-browser preview headers instead of save-as. */
  downloadQualificationDocument: (qualificationId: number, documentId: number, inline = false): Promise<AxiosResponse<Blob>> =>
    api.get(`/core/intelligence/qualifications/${qualificationId}/download-document/`, {
      params: { document: documentId, ...(inline ? { inline: true } : {}) },
      responseType: 'blob',
    }),

  /** Edit a compliance document's metadata (expiry/issue dates, title, type). */
  updateQualificationDocument: (qualificationId: number, documentId: number, data: { expiry_date?: string | null; issue_date?: string | null; title?: string; document_type?: string }): Promise<AxiosResponse<ComplianceDocument>> =>
    api.patch(`/core/intelligence/qualifications/${qualificationId}/update-document/`, { document: documentId, ...data }),

  reviewQualification: (id: number): Promise<AxiosResponse<{ message: string; qualification: SupplierQualification }>> =>
    api.post(`/core/intelligence/qualifications/${id}/review/`),

  approveQualification: (id: number, comments?: string): Promise<AxiosResponse<{ message: string; qualification: SupplierQualification }>> =>
    api.post(`/core/intelligence/qualifications/${id}/approve/`, { comments }),

  rejectQualification: (id: number, comments: string): Promise<AxiosResponse<{ message: string; qualification: SupplierQualification }>> =>
    api.post(`/core/intelligence/qualifications/${id}/reject/`, { comments }),

  requestInfo: (id: number, comments: string): Promise<AxiosResponse<{ message: string; qualification: SupplierQualification }>> =>
    api.post(`/core/intelligence/qualifications/${id}/request_info/`, { comments }),

  calculateScore: (id: number): Promise<AxiosResponse<{ message: string; total_score: string; score_percentage: string; passed: boolean; qualification: SupplierQualification }>> =>
    api.post(`/core/intelligence/qualifications/${id}/calculate_score/`),

  // Responses
  getResponses: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: QualificationResponse[] }>> =>
    api.get('/core/intelligence/responses/', { params }),

  getResponse: (id: number): Promise<AxiosResponse<QualificationResponse>> =>
    api.get(`/core/intelligence/responses/${id}/`),

  createResponse: (data: Partial<QualificationResponse>): Promise<AxiosResponse<QualificationResponse>> =>
    api.post('/core/intelligence/responses/', data),

  updateResponse: (id: number, data: Partial<QualificationResponse>): Promise<AxiosResponse<QualificationResponse>> =>
    api.put(`/core/intelligence/responses/${id}/`, data),

  bulkUpdateResponses: (qualification_id: number, responses: Partial<QualificationResponse>[]): Promise<AxiosResponse<{ message: string; created: number; updated: number }>> =>
    api.post('/core/intelligence/responses/bulk_update/', { qualification_id, responses }),

  uploadResponseFile: (formData: FormData): Promise<AxiosResponse<{ file_path: string; file_name: string }>> =>
    api.post('/core/intelligence/responses/upload_file/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  downloadResponseFile: (qualification: number, question: number): Promise<AxiosResponse<Blob>> =>
    api.get('/core/intelligence/responses/download_file/', {
      params: { qualification, question },
      responseType: 'blob',
    }),

  // Documents
  getDocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ComplianceDocument[] }>> =>
    api.get('/core/intelligence/documents/', { params }),

  getDocument: (id: number): Promise<AxiosResponse<ComplianceDocument>> =>
    api.get(`/core/intelligence/documents/${id}/`),

  createDocument: (data: Partial<ComplianceDocument>): Promise<AxiosResponse<ComplianceDocument>> =>
    api.post('/core/intelligence/documents/', data),

  updateDocument: (id: number, data: Partial<ComplianceDocument>): Promise<AxiosResponse<ComplianceDocument>> =>
    api.put(`/core/intelligence/documents/${id}/`, data),

  deleteDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/intelligence/documents/${id}/`),

  verifyDocument: (id: number): Promise<AxiosResponse<{ message: string; document: ComplianceDocument }>> =>
    api.post(`/core/intelligence/documents/${id}/verify/`),

  getExpiringDocuments: (days?: number): Promise<AxiosResponse<{ count: number; days: number; documents: ComplianceDocument[] }>> =>
    api.get('/core/intelligence/documents/expiring_soon/', { params: { days } }),
};
